import React, { useState, useMemo, useEffect } from 'react'
import { Link, useSearchParams, useNavigate } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { useDashboardData, useCreateApiKey, useRevokeApiKey, useBillingData, useStripePortalSession, useDeleteAccount } from '../services/dashboardQueries'
import { CreateApiKeyModal, RevokeApiKeyModal } from '../components/ui'
import { OverviewTab, ApiDocsTab, BillingTab, AccountTab } from '../components/dashboard'
import toast from '../utils/toast'
import DashboardLoader from '../components/loaders/DashboardLoader'
import { Sparkles } from 'lucide-react'
import {
  DocumentTextIcon,
  CreditCardIcon,
  HomeIcon,
  UserCircleIcon,
  ArrowRightOnRectangleIcon,
  Bars3Icon,
  XMarkIcon,
  CodeBracketIcon,
  Cog6ToothIcon
} from '@heroicons/react/24/outline'
import textBanner from '../assets/images/text-banner-black-min.png'
import { trackApiKeyCreated, trackApiKeyCopied, trackApiKeyRevoked, trackDashboardTabSwitch, trackManagePayment as trackManagePaymentEvent, trackCTAClick } from '../config/analyticsEvents'

const Dashboard = () => {
  const { user, signOut, getAuthToken } = useAuth()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState('overview')
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [showCreateApiKeyModal, setShowCreateApiKeyModal] = useState(false)
  const [createdApiKey, setCreatedApiKey] = useState(null)
  const [showRevokeApiKeyModal, setShowRevokeApiKeyModal] = useState(false)
  const [keyToRevoke, setKeyToRevoke] = useState(null)
  const [showRevokedKeys, setShowRevokedKeys] = useState(false)
  const [revokingKeyId, setRevokingKeyId] = useState(null)

  // Handle 401 Unauthorized by signing out and redirecting to auth
  const handleUnauthorized = React.useCallback(() => {
    // Prevent multiple redirects
    if (window.location.pathname === '/auth') return
    signOut().then(() => {
      navigate('/auth', { replace: true })
    })
  }, [signOut, navigate])

  // Reset scroll position when dashboard mounts
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  // Handle tab query param
  useEffect(() => {
    const tab = searchParams.get('tab')
    if (tab && ['overview', 'api-docs', 'billing', 'account'].includes(tab)) {
      setActiveTab(tab)
    }
  }, [searchParams])
  
  // Fetch dashboard data using React Query
  const { 
    data: dashboardData, 
    isLoading: isDashboardLoading, 
    error: dashboardError,
    isError: isDashboardError,
    refetch: refetchDashboardData
  } = useDashboardData(getAuthToken, user?.id, handleUnauthorized)
  
  // Mutations for API key management
  const createApiKeyMutation = useCreateApiKey(getAuthToken, user?.id, handleUnauthorized)
  const revokeApiKeyMutation = useRevokeApiKey(getAuthToken, user?.id, handleUnauthorized)

  // Fetch billing data using React Query (only when billing tab is active)
  const {
    data: billingData,
    isLoading: isBillingLoading,
  } = useBillingData(getAuthToken, user?.id, handleUnauthorized, {
    enabled: activeTab === 'billing',
  })

  // Stripe portal session mutation
  const stripePortalMutation = useStripePortalSession(getAuthToken, handleUnauthorized)

  // Delete account mutation
  const deleteAccountMutation = useDeleteAccount(getAuthToken, handleUnauthorized)

  // Extract data from API response with fallback values
  const quota = dashboardData?.quota?.current || {}
  const monthlyUsage = dashboardData?.quota?.monthlyUsage || []
  const plan = dashboardData?.plan || {}
  const allApiKeys = dashboardData?.apiKeys?.apiKeys || []
  const userInfo = dashboardData?.user || user

  // Check if user is on Pro plan
  const isPro = plan?.name?.toLowerCase() === 'pro'

  // Handle upgrade button click
  const handleUpgradeClick = () => {
    trackCTAClick('upgrade_to_pro', 'dashboard')
    navigate('/pricing')
  }

  // Sort and filter API keys
  const filteredAndSortedApiKeys = useMemo(() => {
    let keys = [...allApiKeys]
    
    // Filter out revoked keys if not showing them
    if (!showRevokedKeys) {
      keys = keys.filter(key => key.status !== 'revoked')
    }
    
    // Sort keys: active first, then by creation date (newest first)
    keys.sort((a, b) => {
      // First sort by status: active keys come first
      if (a.status === 'active' && b.status !== 'active') return -1
      if (a.status !== 'active' && b.status === 'active') return 1
      
      // Then sort by creation date (newest first)
      return new Date(b.createdAt) - new Date(a.createdAt)
    })
    
    return keys
  }, [allApiKeys, showRevokedKeys])
  
  // Handle API key creation
  const handleCreateApiKey = () => {
    setShowCreateApiKeyModal(true)
  }

  const handleCreateApiKeySubmit = async (keyName) => {
    try {
      const result = await createApiKeyMutation.mutateAsync(keyName)
      trackApiKeyCreated()
      toast.success(`API key "${keyName}" created successfully!`)
      setCreatedApiKey(result)
      return result
    } catch (error) {
      console.error('API key creation error:', error)
      
      // Provide user-friendly error messages for common scenarios
      let userMessage = 'Failed to create API key. Please try again.'
      
      if (error.status === 400) {
        userMessage = 'Invalid API key name. Please choose a different name and try again.'
      } else if (error.status === 401) {
        userMessage = 'Your session has expired. Please log in again to create an API key.'
      } else if (error.status === 403) {
        userMessage = 'You do not have permission to create API keys.'
      } else if (error.status === 429) {
        userMessage = 'Too many requests. Please wait a moment before creating another API key.'
      } else if (error.status >= 500) {
        userMessage = 'Server error occurred. Please try again in a few moments.'
      } else if (error.message && !error.message.includes('API request failed')) {
        userMessage = error.message
      }
      
      toast.error(userMessage)
      throw error
    }
  }

  const handleCloseCreateModal = () => {
    setShowCreateApiKeyModal(false)
    setCreatedApiKey(null)
  }
  
  // Handle API key revocation - show modal
  const handleRevokeApiKey = (keyId, keyName) => {
    setKeyToRevoke({ keyId, keyName })
    setShowRevokeApiKeyModal(true)
  }

  // Confirm API key revocation
  const handleConfirmRevokeApiKey = async () => {
    if (!keyToRevoke) return
    
    try {
      setRevokingKeyId(keyToRevoke.keyId)
      await revokeApiKeyMutation.mutateAsync(keyToRevoke.keyId)
      trackApiKeyRevoked()
      toast.success(`API key "${keyToRevoke.keyName}" has been revoked successfully.`)
      setShowRevokeApiKeyModal(false)
      setKeyToRevoke(null)
    } catch (error) {
      console.error('API key revocation error:', error)
      
      let userMessage = 'Failed to revoke API key. Please try again.'
      
      if (error.status === 400) {
        userMessage = 'Unable to revoke API key. The key may have already been revoked or is invalid.'
      } else if (error.status === 401) {
        userMessage = 'Your session has expired. Please log in again to revoke the API key.'
      } else if (error.status === 403) {
        userMessage = 'You do not have permission to revoke this API key.'
      } else if (error.status === 404) {
        userMessage = 'API key not found. It may have already been revoked.'
      } else if (error.status === 429) {
        userMessage = 'Too many requests. Please wait a moment before trying again.'
      } else if (error.status >= 500) {
        userMessage = 'Server error occurred. Please try again in a few moments.'
      } else if (error.message && !error.message.includes('API request failed')) {
        userMessage = error.message
      }
      
      toast.error(userMessage)
    } finally {
      setRevokingKeyId(null)
    }
  }

  // Close revoke modal
  const handleCloseRevokeModal = () => {
    setShowRevokeApiKeyModal(false)
    setKeyToRevoke(null)
    setRevokingKeyId(null)
  }

  const copyToClipboard = async (text, label = 'Text') => {
    try {
      await navigator.clipboard.writeText(text)
      if (label.toLowerCase().includes('api key') || label.toLowerCase().includes('user id')) {
        trackApiKeyCopied()
      }
      toast.success(`${label} copied to clipboard!`)
    } catch (error) {
      console.error('Failed to copy to clipboard:', error)
      toast.error('Failed to copy to clipboard')
    }
  }

  // Handle Stripe portal session for managing payment
  const handleManagePayment = async () => {
    trackManagePaymentEvent()
    try {
      const result = await stripePortalMutation.mutateAsync()
      if (result?.url) {
        window.location.href = result.url
      } else {
        toast.error('Failed to open payment management portal')
      }
    } catch (error) {
      console.error('Stripe portal error:', error)
      toast.error('Failed to open payment management portal. Please try again.')
    }
  }

  // Handle account deletion
  const handleDeleteAccount = async (captchaToken) => {
    try {
      await deleteAccountMutation.mutateAsync(captchaToken)
      toast.success('Your account has been deleted successfully.')
      // Sign out and redirect to home
      await signOut()
      navigate('/', { replace: true })
    } catch (error) {
      console.error('Delete account error:', error)
      
      let userMessage = 'Failed to delete account. Please try again.'
      
      if (error.status === 401) {
        userMessage = 'Your session has expired. Please log in again.'
      } else if (error.status === 403) {
        userMessage = 'You do not have permission to delete this account.'
      } else if (error.status >= 500) {
        userMessage = 'Server error occurred. Please try again later.'
      } else if (error.message && !error.message.includes('API request failed')) {
        userMessage = error.message
      }
      
      toast.error(userMessage)
      throw error
    }
  }

  // Reset scroll position when tab changes
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [activeTab])
  
  // Show error state
  if (isDashboardError) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">Failed to load dashboard data</p>
          <p className="text-gray-600 text-sm">{dashboardError?.message}</p>
        </div>
      </div>
    )
  }

  const tabs = [
    { id: 'overview', name: 'Overview', icon: HomeIcon, description: 'Dashboard & Usage Statistics' },
    { id: 'api-docs', name: 'Quick Start', icon: CodeBracketIcon, description: 'Api Examples & Integration Guide' },
    { id: 'billing', name: 'Billing & Invoices', icon: CreditCardIcon, description: 'Subscription & Payment Management' },
    { id: 'account', name: 'Account Settings', icon: Cog6ToothIcon, description: 'Security & Account Preferences' }
  ]

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-40">
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              {/* Hamburger menu button for mobile/tablet */}
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="lg:hidden p-2 mr-4 rounded-lg text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors"
              >
                <Bars3Icon className="h-6 w-6" />
              </button>
              
              <Link to="/" className="hover:opacity-80 transition-opacity flex items-center relative">
                <img 
                  src={textBanner} 
                  alt="EasyAppIcon API" 
                  className="h-8"
                />
                <span className="absolute -top-1 -right-8 bg-[#df6e57] text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                  API
                </span>
              </Link>
            </div>
            <div className="flex items-center space-x-8">
              <div className="hidden sm:flex items-center space-x-2 text-gray-700 font-medium">
                <UserCircleIcon className="h-5 w-5" />
                <span>{userInfo?.email || user?.email}</span>
              </div>
              <button
                onClick={signOut}
                className="flex items-center text-gray-700 hover:text-primary-600 transition-colors duration-200 font-medium"
              >
                <ArrowRightOnRectangleIcon className="h-4 w-4 mr-2" />
                Sign Out
              </button>
              {/* Upgrade to Pro button - only show for free plan users */}
              {!isDashboardLoading && !isPro && (
                <button
                  onClick={handleUpgradeClick}
                  className="group relative hidden sm:inline-flex items-center justify-center gap-2 text-white pl-5 pr-5 py-2.5 font-semibold cursor-pointer transition-all duration-300 hover:scale-105"
                >
                  {/* SVG background for price tag shape */}
                  <svg className="absolute inset-0 w-full h-full drop-shadow-lg" viewBox="0 0 160 44" preserveAspectRatio="none">
                    <defs>
                      <linearGradient id="dashboardTagGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor="#3b82f6" />
                        <stop offset="100%" stopColor="#10b981" />
                      </linearGradient>
                      <linearGradient id="dashboardTagGradientHover" x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor="#2563eb" />
                        <stop offset="100%" stopColor="#059669" />
                      </linearGradient>
                    </defs>
                    <path 
                      d="M12 0 H160 V44 H12 L0 22 Z" 
                      fill="url(#dashboardTagGradient)"
                      className="group-hover:hidden"
                    />
                    <path 
                      d="M12 0 H160 V44 H12 L0 22 Z" 
                      fill="url(#dashboardTagGradientHover)"
                      className="hidden group-hover:block"
                    />
                  </svg>
                  <Sparkles className="w-4 h-4 flex-shrink-0 relative z-10" />
                  <span className="relative z-10">Upgrade to Pro</span>
                </button>
              )}
            </div>
          </div>
        </nav>
      </header>

      {/* Backdrop for mobile sidebar */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar - Mobile/Tablet Overlay */}
      <div className={`fixed inset-y-0 left-0 z-50 w-80 bg-white shadow-xl transform transition-transform duration-300 ease-in-out lg:hidden ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Customer Panel</h2>
            <p className="text-sm text-gray-600">Manage your API access and billing</p>
          </div>
          <button
            onClick={() => setSidebarOpen(false)}
            className="p-2 rounded-lg text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>
        
        <nav className="p-4 space-y-2">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => {
                setActiveTab(tab.id)
                trackDashboardTabSwitch(tab.id)
                setSidebarOpen(false)
                if (tab.id === 'overview') {
                  refetchDashboardData()
                }
              }}
              className={`w-full flex items-start px-4 py-3 text-sm font-medium rounded-lg transition-all duration-200 ${
                activeTab === tab.id
                  ? 'bg-blue-50 text-blue-700 border border-blue-200 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50 hover:shadow-sm border border-transparent'
              }`}
            >
              <tab.icon className="h-5 w-5 mr-3 mt-0.5 flex-shrink-0" />
              <div className="text-left">
                <div className="font-medium">{tab.name}</div>
                <div className="text-xs text-gray-500 mt-1">{tab.description}</div>
              </div>
            </button>
          ))}
        </nav>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex">
          {/* Desktop Sidebar - Fixed Position */}
          <div className="hidden lg:flex lg:w-80 lg:flex-shrink-0 py-8">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 w-full h-fit sticky top-24">
              <div className="mb-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-1">Customer Panel</h2>
                <p className="text-sm text-gray-400">Manage your API access and billing</p>
              </div>
              
              <nav className="space-y-2">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => {
                      setActiveTab(tab.id)
                      trackDashboardTabSwitch(tab.id)
                      if (tab.id === 'overview') {
                        refetchDashboardData()
                      }
                    }}
                    className={`w-full flex items-start px-4 py-3 text-sm font-medium rounded-lg transition-all duration-200 ${
                      activeTab === tab.id
                        ? 'bg-blue-50 text-blue-700 border border-blue-200 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50 hover:shadow-sm border border-transparent'
                    }`}
                  >
                    <tab.icon className="h-5 w-5 mr-3 mt-0.5 flex-shrink-0" />
                    <div className="text-left">
                      <div className="font-medium">{tab.name}</div>
                      <div className="text-xs text-gray-400 mt-1">{tab.description}</div>
                    </div>
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1">
            <div className="py-8 lg:pl-8">
            {isDashboardLoading ? (
              <DashboardLoader />
            ) : (
              <>
                {activeTab === 'overview' && (
                  <OverviewTab
                    quota={quota}
                    plan={plan}
                    allApiKeys={allApiKeys}
                    filteredAndSortedApiKeys={filteredAndSortedApiKeys}
                    showRevokedKeys={showRevokedKeys}
                    setShowRevokedKeys={setShowRevokedKeys}
                    handleCreateApiKey={handleCreateApiKey}
                    handleRevokeApiKey={handleRevokeApiKey}
                    createApiKeyMutation={createApiKeyMutation}
                    revokingKeyId={revokingKeyId}
                    copyToClipboard={copyToClipboard}
                    setActiveTab={setActiveTab}
                    userInfo={userInfo}
                  />
                )}

                {activeTab === 'api-docs' && (
                  <ApiDocsTab copyToClipboard={copyToClipboard} userInfo={userInfo} />
                )}

                {activeTab === 'billing' && (
                  <BillingTab 
                    plan={plan}
                    billingData={billingData}
                    isLoadingBilling={isBillingLoading}
                    onManagePayment={handleManagePayment}
                    isManagingPayment={stripePortalMutation.isPending}
                  />
                )}

                {activeTab === 'account' && (
                  <AccountTab
                    userInfo={userInfo}
                    onDeleteAccount={handleDeleteAccount}
                    isDeletingAccount={deleteAccountMutation.isPending}
                  />
                )}
              </>
            )}

            </div>
          </div>
        </div>
      </div>

      {/* Create API Key Modal */}
      <CreateApiKeyModal
        isOpen={showCreateApiKeyModal}
        onClose={handleCloseCreateModal}
        onSubmit={handleCreateApiKeySubmit}
        isLoading={createApiKeyMutation.isPending}
        createdApiKey={createdApiKey}
      />

      {/* Revoke API Key Modal */}
      <RevokeApiKeyModal
        isOpen={showRevokeApiKeyModal}
        onClose={handleCloseRevokeModal}
        onConfirm={handleConfirmRevokeApiKey}
        isLoading={revokingKeyId === keyToRevoke?.keyId}
        keyName={keyToRevoke?.keyName}
      />
    </div>
  )
}

export default Dashboard
